export default defineNuxtPlugin(async () => {
  const route = useRoute();
  if (route.path === '/sign_in' || route.path === '/log-in') return;

  const {ensureValidSession} = useAuthGuard();
  const hasSession = await ensureValidSession(undefined, {redirect: false});
  const session = useSessionStore();

  if (!hasSession) {
    // Signed-out visitors still have a Store cart (framed prints are added to it anonymously), and
    // the header's cart count comes from here.
    if (process.client) {
      session.getUserStoreInfo().catch(() => {});
    }

    return;
  }

  const users = useUsersStore();
  const preferences = usePreferencesStore();
  const appState = useAppStateStore();
  const notifications = useNotificationStore();

  await nextTick();
  await session.getCurrentUser();

  if (process.client) {
    const {hasSessionEnded, onSessionEnded} = useSessionEnd();
    let timer;
    const stopNotificationPolling = () => clearInterval(timer);
    const startNotificationPolling = () => {
      stopNotificationPolling();
      // Also covers an expiry detected while this plugin was still awaiting its setup
      // calls above, i.e. before the onSessionEnded subscription below existed.
      if (hasSessionEnded()) return;
      notifications.getNewCount();
      timer = setInterval(notifications.getNewCount, useRuntimeConfig().public.notificationCountRefreshInterval);
    };

    session.getUserStoreInfo();

    // Auth cookies are shared across tabs, but nothing pushes their changes into this
    // tab's reactive state (e.g. impersonation toggled in another tab). Resync on focus.
    const onVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      const hasSession = await ensureValidSession(undefined, {redirect: false});
      if (!hasSession) return;

      await session.resyncIfChanged();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Session cleanup on expiry: nothing below should keep hitting the API with dead
    // credentials while the expiry notice is up and the app navigates away.
    onSessionEnded(() => {
      stopNotificationPolling();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    });

    if (session.isAuthenticated) {
      await Promise.all([
        preferences.getCurrentUserPreferences(),
        appState.fetchAppState()
      ]);
      session.getDeleteBinSummary();
      users.getManagedConnections();

      setTimeout(startNotificationPolling, 300);
    }

    watch(() => session.currentUser, async (newVal, oldVal) => {
      const didUserChange = newVal?.id !== oldVal?.id;

      await nextTick();

      if (didUserChange) {
        session.getUserStoreInfo();

        if (session.isAuthenticated) {
          startNotificationPolling();
        } else {
          stopNotificationPolling();
        }
      }

      if (session.isAuthenticated) {
        await Promise.all([
          preferences.getCurrentUserPreferences(),
          appState.fetchAppState()
        ]);
        session.getDeleteBinSummary();
        users.getManagedConnections();
      }
    });
  }
});
